import React, { useState, useEffect } from 'react';
import { AppData, Event, Payment, Expense, EventType, PackageType, PaymentType, PaymentMode, ExpenseCategory } from '../types';
import { Plus, ChevronRight, X, Phone, Calendar, Users, IndianRupee, MapPin, Receipt, TrendingDown, Wallet, Trash2, Lock, Pencil } from 'lucide-react';

// --- Shared: Delete Confirmation Modal ---
export interface DeleteProtectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  t: any;
}

export const DeleteProtectionModal: React.FC<DeleteProtectionModalProps> = ({ isOpen, onClose, onConfirm, title, t }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.toLowerCase() === 'ganesha') {
      onConfirm();
      setPassword('');
      setError('');
    } else {
      setError('Incorrect password');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
        <div className="flex items-center gap-3 text-red-600 mb-4">
          <div className="bg-red-100 p-2 rounded-full"><Lock size={20} /></div>
          <h3 className="text-lg font-bold">{t.delete} {title}?</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">{t.password}:</p>
        
        <form onSubmit={handleSubmit}>
          <input 
            type="password" 
            placeholder={t.password}
            className={`w-full border p-3 rounded-lg outline-none mb-2 ${error ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-indigo-500'}`}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError('');
            }}
            autoFocus
          />
          {error && <p className="text-xs text-red-600 font-medium mb-4">{error}</p>}
          
          <div className="flex gap-3 mt-4">
             <button type="button" onClick={onClose} className="flex-1 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium transition">{t.evt_cancel_btn}</button>
             <button type="submit" className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium shadow-md shadow-red-200 transition">{t.confirm}</button>
          </div>
        </form>
      </div>
    </div>
  );
};


// --- Event List Component ---
export const EventList: React.FC<{ data: AppData, onSelectEvent: (e: Event) => void }> = ({ data, onSelectEvent }) => {
  const { t } = data;
  const [filter, setFilter] = useState('All');
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Delete State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<string | null>(null);

  const handleDeleteRequest = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setEventToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (eventToDelete) {
      data.deleteEvent(eventToDelete);
      setDeleteModalOpen(false);
      setEventToDelete(null);
    }
  };

  // New Event Form State
  const [newEvent, setNewEvent] = useState({
    customerName: '',
    phone: '',
    eventType: EventType.WEDDING,
    date: new Date().toISOString().split('T')[0],
    location: '',
    guestCount: 0,
    packageType: PackageType.STANDARD,
    pricePerPlate: 250,
  });

  const handlePackageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const pkgVal = Number(e.target.value);
    setNewEvent(prev => ({
      ...prev,
      packageType: pkgVal,
      pricePerPlate: pkgVal > 0 ? pkgVal : prev.pricePerPlate
    }));
  };

  const handleCreateEvent = () => {
    if (!newEvent.customerName || !newEvent.date) {
      alert("Customer Name and Date are required.");
      return;
    }

    const estimatedTotal = newEvent.guestCount * newEvent.pricePerPlate;
    
    const event: Event = {
      id: Math.random().toString(36).substr(2, 9),
      customerName: newEvent.customerName,
      phone: newEvent.phone,
      eventType: newEvent.eventType as EventType,
      date: newEvent.date,
      location: newEvent.location,
      guestCount: Number(newEvent.guestCount),
      packageType: Number(newEvent.packageType),
      pricePerPlate: Number(newEvent.pricePerPlate),
      estimatedTotal: estimatedTotal,
      finalTotal: estimatedTotal,
      status: 'Upcoming'
    };

    data.addEvent(event);
    setShowCreateModal(false);
    // Reset form
    setNewEvent({
      customerName: '',
      phone: '',
      eventType: EventType.WEDDING,
      date: new Date().toISOString().split('T')[0],
      location: '',
      guestCount: 0,
      packageType: PackageType.STANDARD,
      pricePerPlate: 250,
    });
  };

  const filteredEvents = data.events.filter(e => {
    if (filter === 'All') return true;
    return e.status === filter;
  });

  const filterLabels: any = {
      All: t.evt_filter_all,
      Upcoming: t.evt_filter_up,
      Completed: t.evt_filter_comp,
      Cancelled: t.evt_filter_canc
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">{t.evt_directory}</h2>
        <button 
          type="button"
          onClick={() => setShowCreateModal(true)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-indigo-700 transition shadow-md"
        >
          <Plus size={18} /> {t.evt_new}
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        {['All', 'Upcoming', 'Completed', 'Cancelled'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              filter === f ? 'bg-indigo-600 text-white shadow-md' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            {filterLabels[f]}
          </button>
        ))}
      </div>

      <div className="bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{t.date}</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{t.evt_customer}</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{t.evt_type}</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{t.status}</th>
              <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Balance</th>
              <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider"></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredEvents.map(event => {
              const stats = data.getEventStats(event.id);
              return (
                <tr key={event.id} className="hover:bg-gray-50 transition cursor-pointer group" onClick={() => onSelectEvent(event)}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{event.date}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-gray-900">{event.customerName}</div>
                    <div className="text-xs text-gray-500">{event.guestCount} {t.evt_guests} • {event.location}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{event.eventType}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                      ${event.status === 'Completed' ? 'bg-green-100 text-green-800' : 
                        event.status === 'Upcoming' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'}`}>
                      {event.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium">
                    {stats.balanceDue > 0 ? (
                      <span className="text-red-600 bg-red-50 px-2 py-1 rounded">Due: ₹{stats.balanceDue.toLocaleString()}</span>
                    ) : (
                      <span className="text-green-600 bg-green-50 px-2 py-1 rounded">Paid</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                     <div className="flex items-center justify-end gap-3">
                        <button 
                            onClick={(e) => handleDeleteRequest(event.id, e)}
                            className="text-gray-400 hover:text-red-600 transition p-1 rounded-full hover:bg-red-50"
                        >
                            <Trash2 size={18} />
                        </button>
                        <ChevronRight size={20} className="text-gray-400 group-hover:text-indigo-600" />
                     </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <DeleteProtectionModal 
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Event"
        t={t}
      />

      {/* CREATE EVENT MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden transform transition-all scale-100">
            <div className="bg-indigo-600 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-bold text-white">{t.evt_create_btn}</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-white hover:text-gray-200">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="col-span-2 md:col-span-1">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t.evt_customer}</label>
                <input 
                  type="text" 
                  className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  value={newEvent.customerName}
                  onChange={e => setNewEvent({...newEvent, customerName: e.target.value})}
                  placeholder="e.g. Rajesh Kumar"
                />
              </div>
              
              <div className="col-span-2 md:col-span-1">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t.evt_phone}</label>
                <input 
                  type="tel" 
                  className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  value={newEvent.phone}
                  onChange={e => setNewEvent({...newEvent, phone: e.target.value})}
                  placeholder="e.g. 9876543210"
                />
              </div>

              <div className="col-span-2 md:col-span-1">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t.evt_date}</label>
                <input 
                  type="date" 
                  className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  value={newEvent.date}
                  onChange={e => setNewEvent({...newEvent, date: e.target.value})}
                />
              </div>

              <div className="col-span-2 md:col-span-1">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t.evt_type}</label>
                <select 
                  className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  value={newEvent.eventType}
                  onChange={e => setNewEvent({...newEvent, eventType: e.target.value as EventType})}
                >
                  {Object.values(EventType).map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              <div className="col-span-2">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t.evt_loc}</label>
                <input 
                  type="text" 
                  className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  value={newEvent.location}
                  onChange={e => setNewEvent({...newEvent, location: e.target.value})}
                  placeholder="Venue Name & Address"
                />
              </div>

              <div className="border-t border-gray-100 col-span-2 my-2"></div>
              
              <div className="col-span-2 md:col-span-1">
                 <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t.evt_guests}</label>
                 <input 
                   type="number" 
                   className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                   value={newEvent.guestCount}
                   onChange={e => setNewEvent({...newEvent, guestCount: Number(e.target.value)})}
                 />
              </div>

              <div className="col-span-2 md:col-span-1">
                 <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t.evt_package}</label>
                 <select 
                   className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                   value={newEvent.packageType}
                   onChange={handlePackageChange}
                 >
                   <option value={PackageType.STANDARD}>Standard (₹250)</option>
                   <option value={PackageType.PREMIUM}>Premium (₹350)</option>
                   <option value={PackageType.CUSTOM}>Custom</option>
                 </select>
              </div>

              <div className="col-span-2 md:col-span-1">
                 <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t.evt_price}</label>
                 <input 
                   type="number" 
                   className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                   value={newEvent.pricePerPlate}
                   onChange={e => setNewEvent({...newEvent, pricePerPlate: Number(e.target.value)})}
                   disabled={newEvent.packageType !== PackageType.CUSTOM}
                 />
              </div>

              <div className="col-span-2 md:col-span-1 bg-indigo-50 p-4 rounded-lg border border-indigo-100 flex flex-col justify-center">
                 <span className="text-xs text-indigo-500 uppercase font-bold tracking-wider">{t.evt_est_total}</span>
                 <span className="text-2xl font-bold text-indigo-700">
                    ₹{(newEvent.guestCount * newEvent.pricePerPlate).toLocaleString()}
                 </span>
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
              <button 
                type="button"
                onClick={() => setShowCreateModal(false)} 
                className="px-6 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition font-medium"
              >
                {t.evt_cancel_btn}
              </button>
              <button 
                type="button"
                onClick={handleCreateEvent} 
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition shadow-lg shadow-indigo-200 font-medium"
              >
                {t.evt_create_btn}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Event Details Component ---
export const EventDetail: React.FC<{ event: Event, data: AppData, onBack: () => void }> = ({ event, data, onBack }) => {
  const { t } = data;
  const stats = data.getEventStats(event.id);
  const [activeTab, setActiveTab] = useState<'overview' | 'income' | 'expenses'>('overview');
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  
  // Edit Event State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState<Event>(event);

  // Sync edit form with event prop updates
  useEffect(() => {
    setEditFormData(event);
  }, [event]);
  
  // Delete State
  const [deleteEventModalOpen, setDeleteEventModalOpen] = useState(false);
  const [deleteTxnModalOpen, setDeleteTxnModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{id: string, type: 'payment'|'expense'} | null>(null);

  const handleDeleteEvent = () => {
    setDeleteEventModalOpen(true);
  };

  const handleConfirmDeleteEvent = () => {
    data.deleteEvent(event.id);
    onBack();
  };

  const requestDeletePayment = (id: string) => {
    setItemToDelete({ id, type: 'payment' });
    setDeleteTxnModalOpen(true);
  };

  const requestDeleteExpense = (id: string) => {
    setItemToDelete({ id, type: 'expense' });
    setDeleteTxnModalOpen(true);
  };

  const confirmDeleteItem = () => {
      if(itemToDelete?.type === 'payment') {
          data.deletePayment(itemToDelete.id);
      } else if (itemToDelete?.type === 'expense') {
          data.deleteExpense(itemToDelete.id);
      }
      setItemToDelete(null);
      setDeleteTxnModalOpen(false);
  };

  // Forms State
  const [payAmount, setPayAmount] = useState('');
  const [payMode, setPayMode] = useState(PaymentMode.UPI);
  const [payType, setPayType] = useState(PaymentType.PARTIAL);
  
  const [expCategory, setExpCategory] = useState(ExpenseCategory.VEGETABLES);
  const [expAmount, setExpAmount] = useState('');
  const [expMode, setExpMode] = useState(PaymentMode.CASH);
  const [expPaidBy, setExpPaidBy] = useState('Owner');

  const handleAddPayment = () => {
    if(Number(payAmount) <= 0) return alert("Amount must be greater than 0");
    data.addPayment({
      id: Math.random().toString(36).substr(2, 9),
      eventId: event.id,
      amount: Number(payAmount),
      mode: payMode,
      type: payType,
      date: new Date().toISOString().split('T')[0],
      collectedBy: 'User'
    });
    setShowAddPayment(false);
    setPayAmount('');
  };

  const handleAddExpense = () => {
    if(Number(expAmount) <= 0) return alert("Amount must be greater than 0");
    data.addExpense({
      id: Math.random().toString(36).substr(2, 9),
      eventId: event.id,
      amount: Number(expAmount),
      category: expCategory,
      mode: expMode,
      paidBy: expPaidBy,
      date: new Date().toISOString().split('T')[0]
    });
    setShowAddExpense(false);
    setExpAmount('');
  };

  const handleUpdateEvent = () => {
    data.updateEvent(editFormData);
    setShowEditModal(false);
  };

  const handleEditChange = (field: keyof Event, value: any) => {
    setEditFormData(prev => {
        const updated = { ...prev, [field]: value };
        // Auto-calculate totals if guests or price changes
        if (field === 'guestCount' || field === 'pricePerPlate') {
            updated.estimatedTotal = updated.guestCount * updated.pricePerPlate;
            // Optionally update final total if it matches estimated, 
            // but for editing, we often want to keep finalTotal in sync unless manually overridden?
            // Let's assume strict calculation for now to keep it simple and correct.
            updated.finalTotal = updated.estimatedTotal; 
        }
        return updated;
    });
  };

  const eventPayments = data.payments.filter(p => p.eventId === event.id);
  const eventExpenses = data.expenses.filter(e => e.eventId === event.id);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-4">
        <button onClick={onBack} className="text-gray-500 hover:text-gray-900 flex items-center gap-1 font-medium transition">
          &larr; {t.evt_directory}
        </button>
        <div className="flex gap-2">
            <button 
                onClick={() => setShowEditModal(true)}
                className="text-indigo-600 hover:bg-indigo-50 px-3 py-1.5 rounded-lg flex items-center gap-2 font-medium transition"
            >
                <Pencil size={16} /> {t.evt_edit_btn}
            </button>
            <button 
                onClick={handleDeleteEvent}
                className="text-red-500 hover:bg-red-50 px-3 py-1.5 rounded-lg flex items-center gap-2 font-medium transition"
            >
                <Trash2 size={16} /> {t.delete}
            </button>
        </div>
      </div>

      <DeleteProtectionModal 
        isOpen={deleteEventModalOpen}
        onClose={() => setDeleteEventModalOpen(false)}
        onConfirm={handleConfirmDeleteEvent}
        title="Event"
        t={t}
      />

      <DeleteProtectionModal 
        isOpen={deleteTxnModalOpen}
        onClose={() => setDeleteTxnModalOpen(false)}
        onConfirm={confirmDeleteItem}
        title="Transaction"
        t={t}
      />

      {/* Header Info */}
      <div className="bg-white p-6 rounded-xl shadow-sm mb-6 border-l-8 border-indigo-500">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{event.customerName}</h1>
            <div className="flex flex-wrap gap-4 mt-2 text-gray-600 text-sm font-medium">
              <span className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded"><Calendar size={14}/> {event.date}</span>
              <span className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded"><Phone size={14}/> {event.phone}</span>
              <span className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded"><MapPin size={14}/> {event.location}</span>
              <span className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded"><Users size={14}/> {event.guestCount} Pax</span>
            </div>
          </div>
          <div className="mt-4 md:mt-0 text-left md:text-right">
            <div className="text-sm text-gray-500 font-semibold uppercase tracking-wider">{t.status}</div>
            <div className={`text-xl font-bold ${event.status === 'Completed' ? 'text-green-600' : 'text-blue-600'}`}>{event.status}</div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
         <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
                <Receipt className="text-gray-400" size={16} />
                <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">{t.col_bill}</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">₹{event.finalTotal.toLocaleString()}</p>
         </div>
         <div className="bg-green-50 p-4 rounded-xl border border-green-100 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
                <Wallet className="text-green-600" size={16} />
                <p className="text-xs text-green-700 uppercase font-bold tracking-wider">{t.col_in}</p>
            </div>
            <p className="text-2xl font-bold text-green-700">₹{stats.totalPaid.toLocaleString()}</p>
         </div>
         <div className="bg-red-50 p-4 rounded-xl border border-red-100 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
                <TrendingDown className="text-red-600" size={16} />
                <p className="text-xs text-red-700 uppercase font-bold tracking-wider">{t.col_out}</p>
            </div>
            <p className="text-2xl font-bold text-red-700">₹{stats.totalExpenses.toLocaleString()}</p>
         </div>
         <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
                <IndianRupee className="text-blue-600" size={16} />
                <p className="text-xs text-blue-700 uppercase font-bold tracking-wider">{t.col_profit}</p>
            </div>
            <p className="text-2xl font-bold text-blue-700">₹{stats.netProfit.toLocaleString()}</p>
         </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {['overview', 'income', 'expenses'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`pb-4 px-2 border-b-2 font-bold text-sm capitalize transition ${
                activeTab === tab
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300'
              }`}
            >
              {tab === 'income' ? t.col_in : tab === 'expenses' ? t.col_out : t.dash_subtitle}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
           <div>
             <h3 className="text-lg font-bold text-gray-800 mb-3">Profitability Analysis</h3>
             <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
               <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-gray-500 text-sm font-medium">Cost Per Plate</p>
                    <p className="text-3xl font-bold text-gray-800 mt-1">₹{stats.costPerPlate.toFixed(0)}</p>
                    <p className="text-xs text-gray-400 mt-1">Target: &lt; ₹{(event.pricePerPlate * 0.7).toFixed(0)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm font-medium">Sell Price Per Plate</p>
                    <p className="text-3xl font-bold text-indigo-600 mt-1">₹{event.pricePerPlate}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm font-medium">Profit Margin</p>
                    <div className="flex items-center gap-2">
                       <p className={`text-3xl font-bold mt-1 ${stats.profitMargin > 20 ? 'text-green-500' : 'text-yellow-500'}`}>
                         {stats.profitMargin.toFixed(1)}%
                       </p>
                    </div>
                  </div>
               </div>
               <div className="w-full bg-gray-100 rounded-full h-4 mt-8 overflow-hidden">
                 <div 
                  className={`h-4 rounded-full transition-all duration-1000 ease-out ${stats.profitMargin > 20 ? 'bg-green-500' : 'bg-yellow-500'}`} 
                  style={{ width: `${Math.max(0, Math.min(stats.profitMargin, 100))}%` }}
                 ></div>
               </div>
               <div className="flex justify-between mt-2 text-xs text-gray-400 font-medium">
                  <span>0%</span>
                  <span>Target 30%</span>
                  <span>100%</span>
               </div>
             </div>
           </div>
        </div>
      )}

      {activeTab === 'income' && (
        <div className="space-y-4">
           {/* Ledger Summary */}
           <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <p className="text-xs font-semibold text-gray-500 uppercase">{t.col_bill}</p>
                    <p className="text-xl font-bold text-gray-800 mt-1">₹{event.finalTotal.toLocaleString()}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <p className="text-xs font-semibold text-green-700 uppercase">{t.col_in}</p>
                    <p className="text-xl font-bold text-green-700 mt-1">₹{stats.totalPaid.toLocaleString()}</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <p className="text-xs font-semibold text-red-700 uppercase">Balance Due</p>
                    <p className="text-xl font-bold text-red-700 mt-1">₹{stats.balanceDue.toLocaleString()}</p>
                </div>
           </div>

           <div className="flex justify-between items-center mt-2 mb-4">
             <h3 className="text-lg font-bold text-gray-800">Transaction History</h3>
             <button onClick={() => setShowAddPayment(true)} className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition flex items-center gap-2 shadow-sm">
               <Plus size={16} /> {t.btn_income}
             </button>
           </div>

           <div className="bg-white shadow-sm overflow-hidden rounded-xl border border-gray-200">
             <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{t.date}</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{t.col_event}</th>
                        <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">{t.amount}</th>
                        <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">{t.actions}</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {eventPayments.map(p => (
                        <tr key={p.id} className="hover:bg-gray-50 group">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{p.date}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{p.type}</div>
                                <div className="text-xs text-gray-500">Via {p.mode} • By {p.collectedBy}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    + ₹{p.amount.toLocaleString()}
                                </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                                <button 
                                  onClick={() => requestDeletePayment(p.id)}
                                  className="text-gray-400 hover:text-red-600 transition"
                                  title="Delete Transaction"
                                >
                                  <Trash2 size={16} />
                                </button>
                            </td>
                        </tr>
                    ))}
                    {eventPayments.length === 0 && (
                        <tr>
                            <td colSpan={4} className="px-6 py-12 text-center text-sm text-gray-500">
                                No payments recorded yet.
                            </td>
                        </tr>
                    )}
                </tbody>
             </table>
           </div>
        </div>
      )}

      {activeTab === 'expenses' && (
        <div className="space-y-4">
           {/* Expense Summary */}
           <div className="bg-red-50 p-4 rounded-lg border border-red-200 mb-2">
                <div className="flex justify-between items-center">
                    <div>
                        <p className="text-xs font-semibold text-red-700 uppercase">Total Event Expenses</p>
                        <p className="text-2xl font-bold text-red-800 mt-1">₹{stats.totalExpenses.toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                         <p className="text-xs text-red-600">Items Count</p>
                         <p className="font-bold text-red-800">{eventExpenses.length}</p>
                    </div>
                </div>
           </div>

           <div className="flex justify-between items-center mt-2 mb-4">
             <h3 className="text-lg font-bold text-gray-800">{t.ops_log}</h3>
             <button onClick={() => setShowAddExpense(true)} className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition flex items-center gap-2 shadow-sm">
               <Plus size={16} /> {t.ops_add}
             </button>
           </div>

           <div className="bg-white shadow-sm overflow-hidden rounded-xl border border-gray-200">
             <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{t.date}</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{t.category} & {t.paid_by}</th>
                        <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">{t.amount}</th>
                        <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">{t.actions}</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {eventExpenses.map(e => (
                        <tr key={e.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{e.date}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{e.category}</div>
                                <div className="text-xs text-gray-500">Paid by {e.paidBy} ({e.mode})</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    - ₹{e.amount.toLocaleString()}
                                </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                                <button 
                                  onClick={() => requestDeleteExpense(e.id)}
                                  className="text-gray-400 hover:text-red-600 transition"
                                  title="Delete Expense"
                                >
                                  <Trash2 size={16} />
                                </button>
                            </td>
                        </tr>
                    ))}
                    {eventExpenses.length === 0 && (
                        <tr>
                            <td colSpan={4} className="px-6 py-12 text-center text-sm text-gray-500">
                                No expenses recorded yet.
                            </td>
                        </tr>
                    )}
                </tbody>
             </table>
           </div>
        </div>
      )}

      {/* Edit Event Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden transform transition-all scale-100">
            <div className="bg-indigo-600 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-bold text-white">{t.evt_edit_btn}</h3>
              <button onClick={() => setShowEditModal(false)} className="text-white hover:text-gray-200">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="col-span-2 md:col-span-1">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t.evt_customer}</label>
                <input 
                  type="text" 
                  className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  value={editFormData.customerName}
                  onChange={e => handleEditChange('customerName', e.target.value)}
                />
              </div>
              
              <div className="col-span-2 md:col-span-1">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t.evt_phone}</label>
                <input 
                  type="tel" 
                  className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  value={editFormData.phone}
                  onChange={e => handleEditChange('phone', e.target.value)}
                />
              </div>

              <div className="col-span-2 md:col-span-1">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t.evt_date}</label>
                <input 
                  type="date" 
                  className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  value={editFormData.date}
                  onChange={e => handleEditChange('date', e.target.value)}
                />
              </div>

              <div className="col-span-2 md:col-span-1">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t.evt_type}</label>
                <select 
                  className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  value={editFormData.eventType}
                  onChange={e => handleEditChange('eventType', e.target.value)}
                >
                  {Object.values(EventType).map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              <div className="col-span-2 md:col-span-1">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t.status}</label>
                <select 
                  className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  value={editFormData.status}
                  onChange={e => handleEditChange('status', e.target.value)}
                >
                  <option value="Upcoming">Upcoming</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>

               <div className="col-span-2 md:col-span-1">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t.evt_loc}</label>
                <input 
                  type="text" 
                  className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  value={editFormData.location}
                  onChange={e => handleEditChange('location', e.target.value)}
                />
              </div>

              <div className="border-t border-gray-100 col-span-2 my-2"></div>
              
              <div className="col-span-2 md:col-span-1">
                 <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t.evt_guests}</label>
                 <input 
                   type="number" 
                   className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                   value={editFormData.guestCount}
                   onChange={e => handleEditChange('guestCount', Number(e.target.value))}
                 />
              </div>

              <div className="col-span-2 md:col-span-1">
                 <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t.evt_price}</label>
                 <input 
                   type="number" 
                   className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                   value={editFormData.pricePerPlate}
                   onChange={e => handleEditChange('pricePerPlate', Number(e.target.value))}
                 />
              </div>

               <div className="col-span-2 md:col-span-1">
                 <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t.evt_est_total}</label>
                 <div className="relative">
                    <span className="absolute left-3 top-3 text-gray-400">₹</span>
                    <input 
                    type="number" 
                    className="w-full border border-gray-300 p-3 pl-8 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    value={editFormData.finalTotal}
                    onChange={e => {
                        // Allow manual override
                        setEditFormData({...editFormData, finalTotal: Number(e.target.value)});
                    }}
                    />
                 </div>
                 <p className="text-[10px] text-gray-400 mt-1">Auto-calculated, but you can override.</p>
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
              <button 
                type="button"
                onClick={() => setShowEditModal(false)} 
                className="px-6 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition font-medium"
              >
                {t.evt_cancel_btn}
              </button>
              <button 
                type="button"
                onClick={handleUpdateEvent} 
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition shadow-lg shadow-indigo-200 font-medium"
              >
                {t.evt_save_btn}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showAddPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm transform transition-all scale-100">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-bold text-gray-900">{t.btn_income}</h3>
                <button onClick={() => setShowAddPayment(false)} className="text-gray-400 hover:text-gray-600">
                    <Plus size={24} className="rotate-45" />
                </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">{t.amount}</label>
                <div className="relative">
                    <span className="absolute left-3 top-3 text-gray-400">₹</span>
                    <input type="number" placeholder="0.00" className="w-full border border-gray-300 p-3 pl-8 rounded-lg focus:ring-2 focus:ring-green-500 outline-none" onChange={e => setPayAmount(e.target.value)} value={payAmount} />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">{t.category}</label>
                <select className="w-full border border-gray-300 p-3 rounded-lg bg-gray-50" onChange={e => setPayType(e.target.value as PaymentType)}>
                  {Object.values(PaymentType).map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">{t.mode}</label>
                <select className="w-full border border-gray-300 p-3 rounded-lg bg-gray-50" onChange={e => setPayMode(e.target.value as PaymentMode)}>
                  {Object.values(PaymentMode).map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <button onClick={handleAddPayment} className="w-full mt-6 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition shadow-lg shadow-green-200">{t.evt_save_btn}</button>
          </div>
        </div>
      )}

      {/* Expense Modal */}
      {showAddExpense && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm transform transition-all scale-100">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-bold text-gray-900">{t.ops_add}</h3>
                <button onClick={() => setShowAddExpense(false)} className="text-gray-400 hover:text-gray-600">
                    <Plus size={24} className="rotate-45" />
                </button>
            </div>

             <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">{t.amount}</label>
                <div className="relative">
                    <span className="absolute left-3 top-3 text-gray-400">₹</span>
                    <input type="number" placeholder="0.00" className="w-full border border-gray-300 p-3 pl-8 rounded-lg focus:ring-2 focus:ring-red-500 outline-none" onChange={e => setExpAmount(e.target.value)} value={expAmount} />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">{t.category}</label>
                <select className="w-full border border-gray-300 p-3 rounded-lg bg-gray-50" onChange={e => setExpCategory(e.target.value as ExpenseCategory)}>
                  {Object.values(ExpenseCategory).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">{t.mode}</label>
                    <select className="w-full border border-gray-300 p-3 rounded-lg bg-gray-50" onChange={e => setExpMode(e.target.value as PaymentMode)}>
                    {Object.values(PaymentMode).map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">{t.paid_by}</label>
                    <input type="text" placeholder="e.g. Manager" className="w-full border border-gray-300 p-3 rounded-lg" value={expPaidBy} onChange={e => setExpPaidBy(e.target.value)} />
                  </div>
              </div>
            </div>
            <button onClick={handleAddExpense} className="w-full mt-6 bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition shadow-lg shadow-red-200">{t.evt_save_btn}</button>
          </div>
        </div>
      )}
    </div>
  );
};